import React, { useState, useEffect } from 'react'
import { HandCoins, TrendingUp, TrendingDown, Loader2, Scale, Shield } from 'lucide-react'

const API = process.env.REACT_APP_BACKEND_URL

const fmt = (v) => {
  if (v == null || isNaN(v)) return '--'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v)
}

export default function SuiviAmiableBlock({ tenantId }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exercices, setExercices] = useState([])
  const [annee, setAnnee] = useState(null)

  useEffect(() => {
    if (!tenantId) return
    fetch(`${API}/api/conservation/${tenantId}/exercices`)
      .then(r => r.json())
      .then(d => { setExercices(d.exercices || []); setAnnee(d.annee_courante || new Date().getFullYear()) })
      .catch(() => setAnnee(new Date().getFullYear()))
  }, [tenantId])

  useEffect(() => {
    if (!tenantId || !annee) return
    setLoading(true)
    fetch(`${API}/api/synthese-amiable/${tenantId}/${annee}`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [tenantId, annee])

  const exerciceCourant = exercices.find(e => e.annee === annee)

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    )
  }

  if (!data) return null

  const budgetAmiable = data.budget_amiable || 0
  const chargeOptimisee = data.charge_sinistre_optimisee || 0
  const gainsConvention = data.gains_convention || 0
  const pertesAmiable = data.pertes_amiable || 0
  const totalDroitCommun = data.total_droit_commun || 0
  const totalDroitCommunTTC = data.total_droit_commun_ttc || 0
  const totalEconomie = data.total_economie_amiable || 0
  const nbAmiables = data.nb_amiables || 0
  const nbIrsa = data.nb_irsa || 0
  const nbIrca = data.nb_irca || 0
  const nbDC = data.nb_droit_commun || 0

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="suivi-amiable-block">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <HandCoins size={16} className="text-teal-500" />
          Suivi amiable — {exerciceCourant?.label || annee}
        </h2>
        <div className="flex items-center gap-3">
          {exercices.length > 1 && (
            <select value={annee} onChange={e => setAnnee(Number(e.target.value))} data-testid="suivi-amiable-exercice-select"
              className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-600">
              {exercices.map(ex => <option key={ex.annee} value={ex.annee}>{ex.label}</option>)}
            </select>
          )}
          <span className="text-xs text-slate-400">{nbAmiables} dossier{nbAmiables > 1 ? 's' : ''} amiable{nbAmiables > 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {nbAmiables === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">Aucun dossier amiable sur l'exercice en cours</p>
        ) : (
          <>
            {/* KPI Row - 3 cards */}
            <div className="grid grid-cols-3 gap-3">
              {/* Total montant regle HT */}
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4" data-testid="budget-amiable-kpi">
                <p className="text-[10px] uppercase tracking-wider font-bold text-teal-600 mb-1">Total montant regle HT</p>
                <p className="text-lg font-bold text-teal-800 tabular-nums">{fmt(budgetAmiable)}</p>
                <p className="text-[10px] text-teal-600 mt-1">{nbAmiables} dossiers amiables</p>
              </div>

              {/* Charge sinistre optimisee */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="charge-optimisee-kpi">
                <p className="text-[10px] uppercase tracking-wider font-bold text-blue-600 mb-1">Charge sinistre optimisee</p>
                <div className="flex items-center gap-1.5">
                  <Shield size={16} className="text-blue-600" />
                  <p className="text-lg font-bold text-blue-800 tabular-nums">{fmt(chargeOptimisee)}</p>
                </div>
                <p className="text-[10px] text-blue-600 mt-1">Total charge assureur</p>
              </div>

              {/* Economie totale */}
              <div className={`border rounded-xl p-4 ${
                totalEconomie >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
              }`} data-testid="charge-economisee-kpi">
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${
                  totalEconomie >= 0 ? 'text-emerald-600' : 'text-red-600'
                }`}>Economie nette IRSA/IRCA</p>
                <div className="flex items-center gap-1.5">
                  {totalEconomie >= 0 ? (
                    <TrendingDown size={16} className="text-emerald-600" />
                  ) : (
                    <TrendingUp size={16} className="text-red-600" />
                  )}
                  <p className={`text-lg font-bold tabular-nums ${
                    totalEconomie >= 0 ? 'text-emerald-800' : 'text-red-800'
                  }`}>{fmt(totalEconomie)}</p>
                </div>
                <p className={`text-[10px] mt-1 ${totalEconomie >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  Gains - Pertes conventions
                </p>
              </div>
            </div>

            {/* Detail breakdown */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                <Scale size={12} />
                Detail du calcul
              </p>
              <div className="space-y-1.5">
                {nbIrsa > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">IRSA ({nbIrsa} dossiers x 2 030 EUR)</span>
                    <span className="font-mono font-medium text-blue-700">{fmt(nbIrsa * 2030)}</span>
                  </div>
                )}
                {nbIrca > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">IRCA ({nbIrca} dossier{nbIrca > 1 ? 's' : ''} x 1 600 EUR)</span>
                    <span className="font-mono font-medium text-blue-700">{fmt(nbIrca * 1600)}</span>
                  </div>
                )}
                {totalDroitCommunTTC > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-600">Droit commun ({nbDC} dossier{nbDC > 1 ? 's' : ''} - reel TTC)</span>
                    <span className="font-mono font-medium text-blue-700">{fmt(totalDroitCommunTTC)}</span>
                  </div>
                )}
                <div className="border-t border-slate-200 pt-1.5 mt-1.5 flex justify-between items-center text-sm font-bold">
                  <span className="text-slate-800">Charge sinistre optimisee</span>
                  <span className="font-mono text-blue-700">{fmt(chargeOptimisee)}</span>
                </div>
                <div className="border-t border-slate-100 pt-1.5 mt-1">
                  {gainsConvention > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">Gains conventions IRSA/IRCA</span>
                      <span className="font-mono font-medium text-emerald-700">+{fmt(gainsConvention)}</span>
                    </div>
                  )}
                  {pertesAmiable > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-600">Pertes (amiable &gt; forfait)</span>
                      <span className="font-mono font-medium text-red-700">-{fmt(pertesAmiable)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
